import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import { BoardingOutcomeCode, IBoardingHistory } from "../../interfaces/endpoints/IBoarding.types";
import { getBoardingHistory } from "../../services/Boarding.service";
import { cx } from "../../utils/classnames";
import useCan from "../users/permissions/useCan";

const OUTCOME: Record<BoardingOutcomeCode, { label: string; tone: string }> = {
  boarded: { label: "Skanuar", tone: "bg-green-100 text-green-700" },
  valid: { label: "Kontrolluar", tone: "bg-gray-100 text-gray-600" },
  already_used: { label: "Bileta e përdorur", tone: "bg-red-100 text-red-700" },
  cancelled: { label: "Bileta e anuluar", tone: "bg-red-100 text-red-700" },
  not_paid: { label: "E papaguar", tone: "bg-red-100 text-red-700" },
  wrong_date: { label: "Datë tjetër", tone: "bg-orange-100/30 text-orange-900" },
  wrong_route: { label: "Linjë tjetër", tone: "bg-orange-100/30 text-orange-900" },
  other_operator: { label: "Operator tjetër", tone: "bg-orange-100/30 text-orange-900" },
  unknown_ticket: { label: "Nuk u gjet", tone: "bg-gray-100 text-gray-600" }
};

// The same scanner reading the same code twice within two minutes: recorded, but not a second boarding.
const REPLAYED = 9;
const REPEATED = { label: "Skanim i përsëritur", tone: "bg-gray-100 text-gray-600" };

const CHANNEL: Record<IBoardingHistory["channelCode"], string> = {
  admin_scanner: "Skaner",
  partner_api: "Sistemi i operatorit",
  manual: "Me dorë"
};

// Every scan of this booking, newest first: when, how, by whom and what the scanner was told.
const BoardingHistory: FC<{ uniqueJourneyId: string }> = ({ uniqueJourneyId }) => {
  const canRead = useCan(["reservation.ticket.scan", "reservation.old.ticket.read"]);
  const [rows, setRows] = useState<IBoardingHistory[]>([]);

  useEffect(() => {
    if (!canRead || !uniqueJourneyId) return;
    getBoardingHistory(uniqueJourneyId)
      .then((response) => setRows(response.data ?? []))
      .catch(() => setRows([]));
  }, [canRead, uniqueJourneyId]);

  if (!rows.length) return null;
  return (
    <section className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Skanimet</h3>
      <ul className="flex flex-col gap-2 text-sm">
        {rows.map((row) => {
          const outcome =
            row.outcome === REPLAYED ? REPEATED : OUTCOME[row.outcomeCode] ?? OUTCOME.unknown_ticket;
          return (
            <li key={row.attemptId} className="flex items-center justify-between gap-3">
              <span className="tabular-nums" title={row.deviceRef ?? undefined}>
                {dayjs(row.scannedAt).format("DD.MM.YYYY HH:mm")} · {CHANNEL[row.channelCode]}
                {row.scannedByName ? ` · ${row.scannedByName}` : ""}
              </span>
              {row.revertedAt ? (
                <span
                  className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                  title={row.revertedByName ?? undefined}
                >
                  Skanimi u anulua {dayjs(row.revertedAt).format("HH:mm")}
                </span>
              ) : (
                <span className={cx(["shrink-0 rounded px-2 py-0.5 text-xs font-medium", outcome.tone])}>
                  {outcome.label}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default BoardingHistory;
